/**
 * The prototype service (UI/UX's, Figma's): the board's screens as a prototype (`screens.ts`),
 * the document the Prototype window plays (`html.ts`, with the pictures read once each), the
 * screen it starts from, and the exported site: one HTML file that plays by itself.
 *
 * Nothing here is saved: where a click leads, which screen starts, a screen's device are the
 * board's own data (its items' and frames' `style`), so they are undone, shared and merged like
 * everything else.
 */
import { createEffect, createMemo, createSignal, type Accessor } from "solid-js";
import type { GraphMirror } from "../../client/graph";
import { toBase64 } from "../../shared/base64";
import type { CommandSet } from "../../shared/commands";
import { downloadText } from "../../shared/download";
import type { MenuSection } from "../../shared/menus";
import { toast } from "../../shared/toast";
import type { AssetsService } from "../assets";
import type { Board } from "../board";
import type { View } from "../view";
import { prototypeHtml } from "./html";
import { picturesOf, prototypeOf, type Prototype } from "./screens";

export * from "./html";
export * from "./player";
export * from "./screens";

/** What the service is built from. */
export interface PrototypeDeps {
  graph: Pick<GraphMirror, "state">;
  assets: Pick<AssetsService, "read" | "info">;
  board: Pick<Board, "prototypes" | "selected" | "screens">;
  /** The project's name (the exported file's). */
  title: Accessor<string>;
}

/** The prototype of the board worked in. */
export interface PrototypeService {
  /** The screens and where clicks lead. */
  model: Accessor<Prototype>;
  /** The document the Prototype window plays (no script in it). */
  html: Accessor<string>;
  /** The screen the window starts from (`null`: the prototype's own start). */
  start: Accessor<string | null>;
  setStart(id: string | null): void;
  /** Asks the window to play again from the start (it counts the requests). */
  restarts: Accessor<number>;
  restart(): void;
  /** Saves the prototype as one HTML file that plays by itself; `false` when it has no screen. */
  exportSite(): Promise<boolean>;
}

/** Starts the prototype service. */
export function createPrototype({ graph, assets, board, title }: PrototypeDeps): PrototypeService {
  const model = createMemo(() => prototypeOf(graph.state.graph));
  /** Pictures read: project file → `data:` address (by the file's last change, read again when it changes). */
  const [pictures, setPictures] = createSignal<ReadonlyMap<string, string>>(new Map());
  const asked = new Map<string, string>();
  const [start, setStart] = createSignal<string | null>(null);
  const [restarts, setRestarts] = createSignal(0);

  /** The `data:` address of a project file, read once per change. */
  const read = async (path: string): Promise<string | null> => {
    const content = await assets.read("project", path, true);
    if (!content || !content.info.mime.startsWith("image/")) return null;
    return `data:${content.info.mime};base64,${toBase64(content.bytes)}`;
  };
  createEffect(() => {
    if (!board.prototypes()) return;
    for (const path of picturesOf(model())) {
      const stamp = `${assets.info(path)?.modified_ms ?? 0}`;
      if (asked.get(path) === stamp) continue;
      asked.set(path, stamp);
      void read(path).then((url) => {
        if (!url) return;
        const next = new Map(pictures());
        next.set(path, url);
        setPictures(next);
      });
    }
  });

  const html = createMemo(() => prototypeHtml(model(), { title: title() || "Prototype", pictures: pictures(), start: start() }));

  return {
    model,
    html,
    start: () => (start() && model().screens.some((s) => s.id === start()) ? start() : null),
    setStart,
    restarts,
    restart: () => setRestarts(restarts() + 1),
    async exportSite() {
      const proto = model();
      if (!proto.screens.length) return false;
      // Every picture, even one still being read for the window.
      const all = new Map(pictures());
      for (const path of picturesOf(proto)) if (!all.has(path)) all.set(path, (await read(path)) ?? "");
      const name = (title() || "Prototype").replace(/[\\/:*?"<>|]+/g, " ").trim() || "Prototype";
      downloadText(`${name} prototype.html`, prototypeHtml(proto, { title: `${name}: prototype`, pictures: all, site: true }), "text/html");
      return true;
    },
  };
}

/** The prototype's commands: `prototype.play` opens the Prototype window from the selected screen (or the start). */
export function prototypeCommands(prototype: PrototypeService, board: Pick<Board, "prototypes" | "selected" | "screens">, open: (window: string) => void): CommandSet {
  return {
    "prototype.play": () => {
      if (!board.prototypes()) return void toast("The prototype is UI/UX's: turn on the UI/UX pack and work in it.");
      const chosen = board.selected().find((id) => board.screens().some((s) => s.id === id));
      prototype.setStart(chosen ?? null);
      prototype.restart();
      open("prototype");
    },
  };
}

/** The View menu's prototype lines (where the board makes screens). */
export function prototypeMenus(prototype: PrototypeService, board: Pick<Board, "prototypes">, view: Pick<View, "mode">): MenuSection[] {
  return [
    {
      menu: "view",
      section: "Prototype",
      order: 45,
      entries: () =>
        board.prototypes() && view.mode() === "board"
          ? [
              { label: "Prototype mode", command: "board.prototype" },
              { label: "Play the prototype", command: "prototype.play" },
              { label: "Export the prototype as a site…", action: () => void prototype.exportSite().then((ok) => toast(ok ? "The prototype was saved as one page that plays by itself." : "No screen yet: draw a frame (Shift+F) first.")) },
            ]
          : [],
    },
  ];
}
